import React, { useMemo } from 'react';
import { useAppSelector } from '@redux/store';
import { intlFormatDistance } from 'date-fns/intlFormatDistance';

import {
  INGESTION_CONSTANTS,
  INGESTION_STATUS_LABELS,
} from '../../../utilities/Ingestion.constants';
import { ILogSummaryItem } from '../../../constants/ingestionLog.interface';
import { IDataSourceItem } from 'Modules/Studio/DataSources/redux/initialState';
import { TIngestionMode } from '../../IngestionByDataSource/IngestionByDataSource.constants';
import { IActiveIngestion, TActiveIngestionTables } from '../../../../redux/ingestion.interface';
import { getStatusLabel } from './helpers';
import IngestionStatusIcon from './IngestionStatusIcon';

export type TIngestionStatusLabelProps = {
  small?: boolean;
  status: number | undefined | null;
  mode?: TIngestionMode;
  latestIngestionLog:
    | ILogSummaryItem
    | (IDataSourceItem & { status?: number; startTime?: Date })
    | undefined
    | null;
  activeIngestionTables?: TActiveIngestionTables | undefined;
  activeIngestion: IActiveIngestion | null;
};

export default function IngestionStatusLabel({
  small = false,
  status,
  latestIngestionLog,
  mode,
  activeIngestionTables,
  activeIngestion,
}: TIngestionStatusLabelProps): React.ReactElement<any> | null {
  const view = useAppSelector((state) => state.ingestionDataSourceList.view);

  const label = useMemo(() => {
    return getStatusLabel({
      activeIngestionTables,
      latestIngestionLog,
      status,
      mode,
      view,
      activeIngestion,
    });
  }, [latestIngestionLog, status, view, mode, activeIngestionTables, activeIngestion]);

  if (status === undefined && latestIngestionLog?.status === undefined) {
    return null;
  }

  return (
    <div
      data-testid="ingestion-status-label"
      className={'tw:flex tw:flex-row tw:items-center tw:justify-center'}
    >
      <span className="tw:mr-[11px] tw:text-xs tw:text-secondary-400 tw:font-light tw:text-right">
        {label}

        {!activeIngestion &&
          latestIngestionLog?.startTime &&
          label !== INGESTION_STATUS_LABELS[INGESTION_CONSTANTS.RUNNING] &&
          label !== INGESTION_STATUS_LABELS[INGESTION_CONSTANTS.TRIGGERED_STOP] && (
            <>
              <br />
              {intlFormatDistance(new Date(latestIngestionLog.startTime), new Date(), {
                style: 'narrow',
              })}
            </>
          )}
      </span>
      <IngestionStatusIcon
        activeIngestionTables={activeIngestionTables}
        mode={mode}
        status={status}
        small={small}
        latestIngestionLog={latestIngestionLog}
        activeIngestion={activeIngestion}
      />
    </div>
  );
}
